import { useAuthStore } from '@/auth/useAuthStore'
import { logError } from '@/logger'
import { OlympusClient } from '@/OlympusClient'
import { useFeatureFlags } from '@/utils/useFeatureFlags'

const getFeatureFlagUserId = (): string | null | undefined => {
  const authStore = useAuthStore()

  return authStore.user?.email ?? authStore.user?.id
}

// Resolves the brand's derived Unleash gating properties (e.g. `aiLedBrand`) as
// string-valued context fields so flags can be gated with a `<key> IN (true)`
// constraint. Adding a new gate is backend-only — a new resolver flows through
// here untouched. Failures fall back to an empty map so a backend hiccup never
// spuriously grants a gated feature.
const getBrandGatingContext = async (
  brandId: string
): Promise<Record<string, string>> => {
  try {
    const context = await OlympusClient.launch.v1.brands.brand.getGatingContext(
      {
        params: { brandId },
      }
    )
    return Object.fromEntries(
      Object.entries(context).map(([key, value]) => [key, String(value)])
    )
  } catch (error) {
    logError(error, { brandId, context: 'refreshFeatureFlagsForBrand.gating' })
    return {}
  }
}

export const refreshFeatureFlagsForBrand = async (
  brandId: string | undefined
): Promise<void> => {
  if (!brandId) {
    return
  }

  const featureFlags = useFeatureFlags()
  const context = featureFlags.getContext()
  const userId = getFeatureFlagUserId()
  const resolvedUserId = userId ?? context.userId

  if (
    context.properties?.brandId === brandId &&
    context.userId === resolvedUserId &&
    featureFlags.isReady()
  ) {
    return
  }

  try {
    const gatingContext = await getBrandGatingContext(brandId)
    await featureFlags.updateContext({
      currentTime: context.currentTime,
      properties: {
        ...context.properties,
        ...gatingContext,
        brandId,
      },
      remoteAddress: context.remoteAddress,
      sessionId: context.sessionId,
      userId: resolvedUserId,
    })
  } catch (error) {
    logError(error, {
      brandId,
      context: 'refreshFeatureFlagsForBrand',
    })
  }
}
